"use client"

import * as React from "react"
import { useLocation } from "react-router"
import { PageHeader } from "@/components/page-header"
import {
  ListPageViewFrame,
  LIST_PAGE_VIEW_FRAME_MAX_WIDE,
} from "@/components/data-views/list-page-view-frame"
import { EVAL_DASHBOARD_KPI_A11Y_CLASS } from "@/lib/evaluation-dashboard-a11y"
import { EvaluationDashboardLifecycleView } from "./evaluation-dashboard-lifecycle-view"
import { EvaluationDashboardActionView } from "./evaluation-dashboard-action-view"
import { EvaluationDashboardAnalyticView } from "./evaluation-dashboard-analytic-view"
import { EvaluationDashboardQualityView } from "./evaluation-dashboard-quality-view"
import { EvaluationDashboardOperationsView } from "./evaluation-dashboard-operations-view"
import { EvaluationDashboardOnboardingSection } from "./evaluation-dashboard-onboarding-section"
import { EvaluationDashboardOnboardingWelcomeRail } from "./evaluation-dashboard-onboarding-welcome-rail"
import { EvaluationDashboardOperationsV2View } from "./evaluation-dashboard-operations-v2-view"
import { EvaluationDashboardOperationsV3View } from "./evaluation-dashboard-operations-v3-view"
import { QualityTermSelector } from "./quality/quality-term-bar"
import { OtherTermsSection } from "./all-terms-section"
import { getDashboardTermSlice } from "@/lib/evaluation-dashboard-context"
import { surveysOnboardingOperationsDemo } from "@/lib/evaluation-dashboard-onboarding"
import { surveysDashboardLeoInsight } from "@/lib/surveys-dashboard-leo-insight"
import { useEvaluationDashboardApproach } from "@/hooks/use-evaluation-dashboard-approach"
import { useEvaluationDashboardScenario } from "@/hooks/use-evaluation-dashboard-scenario"
import { useSurveysFirstTermSetup } from "@/hooks/use-surveys-first-term-setup"
import { useProduct } from "@/contexts/product-context"
import { useAskLeoPageContext } from "@/components/ask-leo-context"
import { cn } from "@/lib/utils"

function demoSearch(demo: ReturnType<typeof surveysOnboardingOperationsDemo>): string {
  return demo ? `?demo=${demo}` : ""
}

const SURVEYS_DASHBOARD_PANE_HEIGHT = "--surveys-dashboard-pane-height"

/**
 * Size the two dashboard columns to the visible `[data-page-scroll]` pane.
 * A 100dvh calc overshoots that pane (utility bar is already subtracted there),
 * so the rail clips with no scrollbar.
 */
function useSurveysDashboardPaneHeight(enabled: boolean) {
  const rootRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    if (!enabled) return
    const root = rootRef.current
    const scrollport = root?.closest("[data-page-scroll]")
    if (!root || !(scrollport instanceof HTMLElement)) return

    const apply = () => {
      const header = root.firstElementChild
      const scrollRect = scrollport.getBoundingClientRect()
      const headerBottom =
        header instanceof HTMLElement
          ? header.getBoundingClientRect().bottom
          : root.getBoundingClientRect().top
      const pane = Math.floor(scrollRect.bottom - headerBottom - 16)
      root.style.setProperty(SURVEYS_DASHBOARD_PANE_HEIGHT, `${Math.max(0, pane)}px`)
    }

    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(scrollport)
    observer.observe(root)
    window.addEventListener("resize", apply)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", apply)
      root.style.removeProperty(SURVEYS_DASHBOARD_PANE_HEIGHT)
    }
  }, [enabled])

  return rootRef
}

export function EvaluationDashboard() {
  const { search } = useLocation()
  const { product } = useProduct()
  const approach = useEvaluationDashboardApproach()
  const scenario = useEvaluationDashboardScenario()
  const setup = useSurveysFirstTermSetup(product)

  const operationsStage = setup.operationsStage
  const isSurveysOperations = approach === "operations" && product === "exxat-surveys"
  const operationsDemo = isSurveysOperations
    ? surveysOnboardingOperationsDemo(operationsStage)
    : null

  const termSlice = React.useMemo(
    () =>
      getDashboardTermSlice(
        isSurveysOperations ? demoSearch(operationsDemo) : search,
        undefined,
        scenario,
      ),
    [isSurveysOperations, operationsDemo, scenario, search],
  )
  const {
    showNoTerms,
    currentTerm,
    lastTerm,
    pastTerms,
    upcomingTerms,
    futureTerms,
    allTerms,
  } = termSlice

  const showOnboardingLayer = isSurveysOperations && setup.showOnboardingLayer
  const showStandaloneOperations = approach === "operations" && !isSurveysOperations
  const showOtherTerms =
    !showNoTerms &&
    approach !== "operations-v2" &&
    approach !== "operations-v3" &&
    approach !== "operations"
  const railPhase =
    setup.showSetupGuide && !setup.isChecklistComplete ? "setup" : "after"

  useAskLeoPageContext(
    React.useMemo(
      () => ({
        title: "Dashboard",
        description: "Term lifecycle overview for scheduling surveys and closed term analytics.",
        suggestions: [
          "Which courses still need evaluations scheduled?",
          "Summarize response rates for the current term",
          "Which faculty scored below threshold last term?",
        ],
      }),
      [],
    ),
  )

  const paneRootRef = useSurveysDashboardPaneHeight(isSurveysOperations)
  const paneColumnClassName = isSurveysOperations
    ? "lg:h-[var(--surveys-dashboard-pane-height,auto)] lg:overflow-y-auto lg:px-1.5"
    : undefined

  return (
    <div
      ref={paneRootRef}
      className={cn(
        "flex flex-col gap-3 pb-8",
        isSurveysOperations && "lg:pb-0",
        EVAL_DASHBOARD_KPI_A11Y_CLASS,
      )}
    >
      <PageHeader
        title="Dashboard"
        actions={approach === "quality" && !showNoTerms ? <QualityTermSelector /> : undefined}
        className="shrink-0 pb-0 lg:pb-1"
      />

      <div className="flex flex-col gap-10 px-4 lg:px-6">
        {approach === "lifecycle" ? (
          <EvaluationDashboardLifecycleView
            showNoTerms={showNoTerms}
            currentTerm={currentTerm}
            lastTerm={lastTerm}
            pastTerms={pastTerms}
            upcomingTerms={upcomingTerms}
            futureTerms={futureTerms}
          />
        ) : null}

        {approach === "action" && !showNoTerms ? (
          <EvaluationDashboardActionView currentTerm={currentTerm} lastTerm={lastTerm} />
        ) : null}

        {approach === "analytic" && !showNoTerms ? (
          <EvaluationDashboardAnalyticView
            currentTerm={currentTerm}
            lastTerm={lastTerm}
            pastTerms={pastTerms}
          />
        ) : null}

        {approach === "quality" && !showNoTerms ? (
          <EvaluationDashboardQualityView />
        ) : null}

        {isSurveysOperations ? (
          <ListPageViewFrame
            maxWidthClassName={LIST_PAGE_VIEW_FRAME_MAX_WIDE}
            gutterClassName="w-full min-w-0"
          >
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-start">
              <div className={cn("flex min-w-0 flex-col gap-6", paneColumnClassName)}>
                {showOnboardingLayer ? (
                  <EvaluationDashboardOnboardingSection
                    product={product}
                    setup={setup}
                    currentTerm={currentTerm}
                  />
                ) : null}

                {operationsStage !== "hidden" ? (
                  <EvaluationDashboardOperationsView
                    currentTerm={currentTerm}
                    demo={operationsDemo}
                    reveal={operationsStage}
                  />
                ) : null}

                {operationsStage === "full" && !showNoTerms ? (
                  <OtherTermsSection
                    pastTerms={pastTerms}
                    upcomingTerms={upcomingTerms}
                    futureTerms={futureTerms}
                  />
                ) : null}
              </div>

              <div className={cn("min-w-0", paneColumnClassName)}>
                <EvaluationDashboardOnboardingWelcomeRail
                  phase={railPhase}
                  insight={surveysDashboardLeoInsight(
                    operationsStage,
                    setup.steps.findIndex((_, index) => !setup.completed.has(index)),
                  )}
                />
              </div>
            </div>
          </ListPageViewFrame>
        ) : null}

        {showStandaloneOperations ? (
          <EvaluationDashboardOperationsView
            currentTerm={currentTerm}
            demo={operationsDemo}
            reveal={operationsStage === "collecting" ? "collecting" : "full"}
          />
        ) : null}

        {approach === "operations-v2" && !showNoTerms ? (
          <EvaluationDashboardOperationsV2View
            currentTerm={currentTerm}
            lastTerm={lastTerm}
            upcomingTerms={upcomingTerms}
            futureTerms={futureTerms}
            allTerms={allTerms}
          />
        ) : null}

        {approach === "operations-v3" && !showNoTerms ? (
          <EvaluationDashboardOperationsV3View
            currentTerm={currentTerm}
            pastTerms={pastTerms}
            upcomingTerms={upcomingTerms}
            futureTerms={futureTerms}
          />
        ) : null}

        {showNoTerms &&
        approach !== "lifecycle" &&
        approach !== "quality" &&
        approach !== "operations" ? (
          <EvaluationDashboardLifecycleView
            showNoTerms
            pastTerms={[]}
            upcomingTerms={[]}
            futureTerms={[]}
          />
        ) : null}

        {showOtherTerms ? (
          <OtherTermsSection
            pastTerms={pastTerms}
            upcomingTerms={upcomingTerms}
            futureTerms={futureTerms}
          />
        ) : null}
      </div>
    </div>
  )
}
